import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Chat as ChatIcon,
  Security as SecurityIcon,
  ContactSupport as ContactSupportIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as DRBFMIcon,
  Checklist as VerificationIcon,
  ViewInAr as CADIcon,
  MonitorHeart as AuditIcon,
} from '@mui/icons-material';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const HelpPage: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqItems: FAQItem[] = [
    {
      question: t('help.faq.searchQuestion', 'ドキュメントを検索するにはどうすればいいですか？'),
      answer: t('help.faq.searchAnswer', 'サイドバーの「検索」メニューから検索ページにアクセスできます。キーワードや自然文を入力すると、関連する文書、抜粋、参照元ページを確認できます。'),
      category: 'search',
    },
    {
      question: t('help.faq.uploadQuestion', 'ドキュメントをアップロードするにはどうすればいいですか？'),
      answer: t('help.faq.uploadAnswer', 'サイドバーの「ドキュメントをアップロード」をクリックし、ファイルを選択してください。PDF、DOCX、XLSX、PPTX形式に対応しています。AI（GPT-4）がタイトル・要約・カテゴリ・タグを自動抽出し、図面や画像も含めて全文検索できるようインデックス化します。'),
      category: 'upload',
    },
    {
      question: t('help.faq.chatQuestion', 'AIチャット機能の使い方を教えてください'),
      answer: t('help.faq.chatAnswer', '画面右上のチャットアイコンからチャットパネルを開けます。ドキュメントに関する質問をすると、関連情報を検索して回答します。回答カードでは参照文書の関連度や根拠を確認できます。'),
      category: 'chat',
    },
    {
      question: t('help.faq.chatFileScopeQuestion', '特定のファイルだけを対象にチャットしたいときは？'),
      answer: t('help.faq.chatFileScopeAnswer', 'チャット入力欄の「コンテキスト追加」ボタンから対象ドキュメントを選択すると、そのファイルに限定した Q&A ができます。検索結果カードのチェックボックスからも同じ挙動でコンテキストに追加できます。'),
      category: 'chat',
    },
    {
      question: t('help.faq.drbfmQuestion', 'DRBFM分析機能はどのように使いますか？'),
      answer: t('help.faq.drbfmAnswer', 'サイドバーの「DRBFM分析」からアクセスできます。部品名と旧／新仕様書を入力すると、AI が設計変更点・心配点・対策案を生成します。故障モードの関連知識は実ファイルリンク・抜粋・関連度付きで確認できます。'),
      category: 'drbfm',
    },
    {
      question: t('help.faq.verificationQuestion', '設計検証項目一覧の使い方を教えてください'),
      answer: t('help.faq.verificationAnswer', 'サイドバーの「設計検証項目」からアクセスできます。検証セッションを作成し、ユニット（大分類）と検証項目を追加していきます。分類（性能・機能・信頼性など）と検証方法をマトリクス形式で管理でき、Excel出力や DRBFM からの自動変換にも対応しています。'),
      category: 'verification',
    },
    {
      question: t('help.faq.cadQuestion', '3D CAD ビューアでは何ができますか？'),
      answer: t('help.faq.cadAnswer', 'サイドバーの「3D CADビューア」から STEP / STL / IGES 形式をブラウザ上で 3D 表示できます。旧新図面の比較に加えて、DRBFMの故障モードとCAD上の位置をつなげて確認できます。'),
      category: 'cad',
    },
    {
      question: t('help.faq.categoryQuestion', 'ドキュメントのカテゴリーはどのように選べばいいですか？'),
      answer: t('help.faq.categoryAnswer', 'アップロード時にカテゴリーを選択できます。「設計基準書」「過去トラブル」「技術仕様書」「運用手順書」「一般」から適切なものを選んでください。AIが自動判定することもできます。'),
      category: 'upload',
    },
    {
      question: t('help.faq.auditQuestion', '自分や組織の操作履歴はどこで確認できますか？'),
      answer: t('help.faq.auditAnswer', 'manager・admin ロールは「設定」画面の「アクティビティ・監査ログ」から、あるいは URL `/admin/activity` から、テナント内の操作履歴（ログイン／ドキュメント／チャット／CAD／DRBFM ほか）を確認できます。admin は同画面から監査ログを CSV エクスポートでき、`/admin/anomalies` では異常検知イベント（連続ログイン失敗・テナント境界違反など）の triage と解決済みマーキングが行えます。一般ユーザーに対しては権限上の理由で表示されません。'),
      category: 'audit',
    },
    {
      question: t('help.faq.securityQuestion', 'ドキュメントのセキュリティはどうなっていますか？'),
      answer: t('help.faq.securityAnswer', 'テナント単位でアクセスが分離されており、許可されたユーザーのみがドキュメントにアクセスできます。JWT 認証と MFA（多要素認証）に対応し、主要操作は監査ログに記録されます。異常検知（連続ログイン失敗・テナント境界違反など）は Slack 通知と App Insights への構造化ログ送出で運用チームが監視しています。'),
      category: 'security',
    },
  ];

  const features = [
    { icon: <SearchIcon />, title: t('help.features.search', 'AIセマンティック検索'), description: t('help.features.searchDesc', 'キーワードや自然文で検索し、関連文書、抜粋、参照元ページを確認できます。') },
    { icon: <UploadIcon />, title: t('help.features.upload', 'ドキュメントアップロード'), description: t('help.features.uploadDesc', 'PDF、DOCX、XLSX、PPTX 形式に対応。AI がタイトル・要約・カテゴリ・タグを自動抽出し、図面や画像も含めて全文検索可能にします。') },
    { icon: <ChatIcon />, title: t('help.features.chat', 'AIチャットアシスタント'), description: t('help.features.chatDesc', 'ドキュメントに関する質問に AI が回答。ファイル単位のスコープ指定、Web検索の切り替え、参照文書の関連度や根拠を確認できます。') },
    { icon: <DRBFMIcon />, title: t('help.features.drbfm', 'DRBFM分析'), description: t('help.features.drbfmDesc', '旧／新仕様書の比較から心配点・対策を生成。関連知識を実ファイルリンク・抜粋・関連度付きで確認できます。') },
    { icon: <VerificationIcon />, title: t('help.features.verification', '設計検証項目一覧'), description: t('help.features.verificationDesc', '検証項目の作成・管理・Excel 出力に対応。DRBFM 分析結果から検証項目を生成し、元の分析結果へ戻って確認できます。') },
    { icon: <CADIcon />, title: t('help.features.cad', '3D CAD ビューア'), description: t('help.features.cadDesc', 'STEP / STL / IGES をブラウザ上で 3D 表示。旧新図面の比較や、DRBFMの故障モードとCAD上の位置をつなげて確認できます。') },
    { icon: <AuditIcon />, title: t('help.features.audit', 'アクティビティ・監査ログ（管理者向け）'), description: t('help.features.auditDesc', 'ログイン／ドキュメント／チャット／CAD／DRBFM など主要操作を記録。監査ログ、異常検知、CSVエクスポートを管理画面から確認できます。') },
    { icon: <SecurityIcon />, title: t('help.features.security', 'セキュアな文書管理'), description: t('help.features.securityDesc', 'テナント単位のアクセス制御、JWT 認証、MFA、監査ログ、異常検知通知を組み合わせた多層的な安全対策を提供します。') },
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <HelpIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h4">{t('help.title', 'ヘルプ')}</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('help.description', 'AIONA の使い方とよくある質問')}
      </Typography>

      {/* Features Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <BookIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('help.featuresTitle', '主な機能')}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List>
            {features.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>{feature.icon}</ListItemIcon>
                <ListItemText
                  primary={feature.title}
                  secondary={feature.description}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ContactSupportIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('help.faqTitle', 'よくある質問')}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {faqItems.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleAccordionChange(`panel${index}`)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    size="small"
                    label={faq.category}
                    color={
                      faq.category === 'search' ? 'primary' :
                      faq.category === 'upload' ? 'success' :
                      faq.category === 'chat' ? 'info' :
                      faq.category === 'drbfm' ? 'warning' :
                      faq.category === 'verification' ? 'secondary' :
                      faq.category === 'cad' ? 'info' :
                      faq.category === 'audit' ? 'warning' :
                      faq.category === 'security' ? 'error' : 'default'
                    }
                    sx={{ minWidth: 70 }}
                  />
                  <Typography>{faq.question}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircleIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('help.tipsTitle', 'クイックヒント')}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('help.tip1', '検索時は具体的なキーワードを使うと、より正確な結果が得られます')} />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('help.tip2', 'ドキュメントにタグを追加すると、後で見つけやすくなります')} />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('help.tip3', 'AIチャットでは、質問を具体的にすると良い回答が得られます')} />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('help.tip4', 'サイドバーはミニモードに切り替えると、作業スペースを広く使えます')} />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('help.tip5', '設計検証項目一覧はExcelに出力して、チーム内で共有できます')} />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Contact Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('help.contactTitle', 'お問い合わせ')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('help.contactDescription', 'その他のご質問やサポートが必要な場合は、システム管理者にお問い合わせください。')}
          </Typography>
          <Box mt={2}>
            <Link href="mailto:info@aiona.co.jp" color="primary">
              info@aiona.co.jp
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HelpPage;
